import { onRequest } from '../..';
import { ActionsType, PropertyType } from '../../../common';
import { getCasinoGames } from '../../../api/endpoints/getCasinoGames';
import { activateGames, deactivateGames, getFilterOptions } from '../../../api';
import { v4 as uuid } from 'uuid';
import { find, findIndex } from 'lodash';
import { addNewGame } from '../../../api/endpoints/addNewGame';
import { importGames } from '../../../api/endpoints/importGames';
import {
	validateBulkAssignPartnersForm,
	validateEnableGamesForm,
	validateForm,
} from '../../../view/pages/Games/Games.helper';
import { validateImportForm } from '../../../view/pages/Games/Games.helper';
import { GameFormKey } from '../../../view/pages/Games/Games.const';
import { getCasinoGame } from '../../../api/endpoints/getCasinoGame';
import { editGame } from '../../../api/endpoints/editGame';
import { addGameCategory } from '../../../api/endpoints/addGameCategory';
import { assignGamePartners } from '../../../api/endpoints/assignGamePartners';
import { getCasinoGamePartners } from '../../../api/endpoints/getCasinoGamePartners';
import { bulkAssignPartners } from '../../../api/endpoints/bulkAssignPartners';
import { bulkEnableGames } from '../../../api/endpoints/bulkEnableGames';
import { unassignGamePartners } from '../../../api/endpoints/unassignGamePartners';

const gamesInit = async (payload: PropertyType) => {
	const { token, page, perPage } = payload;
	const { games, pagesTotal } = await getCasinoGames({ token, page, query: '', perPage });
	return { games: games, bulkRows: [], pagesTotal, currentPage: page, gamesLoading: false };
};

const gameInit = async (state: PropertyType, payload: PropertyType) => {
	const { token, id } = payload;
	const { AAMS_types, providers } = state;
	const { game } = await getCasinoGame({ token, id });
	const selectedAAMS = find(AAMS_types, ['value', game.aams_type_id]);
	const selectedProvider = find(providers, ['value', game.game_provider_id]);

	const gameData = {
		game_name: game.game_name,
		custom_game_id: game.custom_game_id,
		notes: game.notes,
		game_provider_id: selectedProvider ? [selectedProvider] : [],
		provider_game_id_desktop: game.provider_game_id_desktop || '',
		provider_game_id_mobile: game.provider_game_id_mobile || '',
		aams_type_id: selectedAAMS ? [selectedAAMS] : [],
		rtp: game.rtp ?? '',
		volatility: game.volatility ?? '',
		wagering_weight: game.wagering_weight ?? '',
		is_slot: !!game.is_slot,
		active: !!game.active,
		custom_fields: game.custom_fields.map((f: PropertyType) => ({ ...f, id: uuid() })),
		assigned_partners: game.assigned_partners,
	};

	const categories = {
		categories: game.game_categories.map((o: PropertyType) => ({ value: +o.id, label: o.name, isChecked: true })),
	};

	return { gamesLoading: false, gameData: gameData, gameForm: gameData, categoryForm: categories };
};

const partnersInit = (user: PropertyType) => {
	const { partners } = user;

	return { partners };
};
const getFiltersUrl = (filters: PropertyType[]) => {
	const urlFragments = filters.map((f) => {
		const name = f.name === 'checksumStatuses' ? 'checksum_last_statuses' : f.name;
		return `${name}=${f.value}`;
	});

	return `?${urlFragments.join('&')}`;
};

const filterData = async (token: string, page: string, filters: PropertyType[], perPage: string) => {
	const query = filters.length ? getFiltersUrl(filters) : '';
	const data = await getCasinoGames({ token, page, query, perPage });

	return {
		...data,
		filters,
		filtering: filters.length > 1,
		bulkRows: [],
		currentPage: page,
		gamesLoading: false,
	};
};

const onBulkChange = (state: PropertyType, id: string) => {
	const selectedRows = [...state.bulkRows];
	const isSelected = selectedRows.includes(id);

	isSelected ? selectedRows.splice(selectedRows.indexOf(id), 1) : selectedRows.push(id);

	return { bulkRows: selectedRows };
};

const onBulkAllChange = (state: PropertyType) => {
	const { games, bulkRows } = state;

	if (bulkRows.length === games.length) return { bulkRows: [] };
	return { bulkRows: games.map((g: PropertyType) => g?.internal_game_id?.toString()) };
};

const filtersInit = async (payload: PropertyType) => {
	const { token, filters } = payload;
	const resp = await getFilterOptions({ token, filters });

	return { ...resp.data };
};

const onFilter = async (state: PropertyType, payload: PropertyType) => {
	const { token, page, filters } = payload;

	return filterData(token, page, filters, state.perPage);
};

const onPageUpdate = async (state: PropertyType, payload: PropertyType) => {
	const { token, page, perPage } = payload;
	return filterData(token, page, state.filters, perPage);
};

const onGameToggle = async (payload: PropertyType) => {
	const { token, page } = payload;
	const data = await getCasinoGames({ token, page, query: '' });

	return { data };
};

const onUpdateCategoryForm = (state: PropertyType, payload: PropertyType) => {
	const { categories } = payload;
	return { categoryForm: { categories: categories } };
};

const onUpdateGamePartnersForm = (state: PropertyType, payload: PropertyType) => {
	const { activePartners } = payload;
	return { gamePartnersForm: { gamePartners: activePartners } };
};

const formReset = () => {
	return {
		gameForm: {
			game_name: '',
			custom_game_id: '',
			notes: '',
			game_provider_id: [],
			provider_game_id_desktop: '',
			provider_game_id_mobile: '',
			aams_type_id: [],
			rtp: '',
			volatility: '',
			wagering_weight: '',
			is_slot: false,
			active: false,
			custom_fields: [],
		},
	};
};

const importFormReset = () => {
	return {
		importGameForm: {
			file: '',
		},
	};
};

const bulkAssignPartnersFormReset = () => {
	return {
		bulkAssignPartnersForm: {
			file: '',
		},
	};
};

const bulkEnableGamesFormReset = () => {
	return {
		bulkEnableGamesForm: {
			file: '',
		},
	};
};

const onGameAdd = async (state: PropertyType, payload: PropertyType) => {
	const { token, gameForm } = payload;
	const formErrors = validateForm(gameForm);
	const games = [...state.games];
	let responesMsg = '';
	if (formErrors) {
		return { formErrors };
	} else {
		if (state?.selectedGame?.internal_game_id) {
			const { game, message } = await editGame({
				token,
				gameForm: { ...gameForm, internal_game_id: state.selectedGame.internal_game_id },
			});
			responesMsg = message;
			const editIndex = findIndex(games, ['internal_game_id', state.selectedGame.internal_game_id]);
			games[editIndex] = game;
		} else {
			const { game, message } = await addNewGame({ token, gameForm });
			responesMsg = message;
			games.unshift(game);
		}
		return {
			globalSuccess: responesMsg,
			games,
		};
	}
};

const onGamesImport = async (state: PropertyType, payload: PropertyType) => {
	const { token, importGameForm } = payload;
	const formErrors = validateImportForm(importGameForm);
	let responesMsg = '';
	if (formErrors) {
		return { formErrors };
	} else {
		const { message, data } = await importGames({
			token,
			importGameForm,
		});

		if (!data.errors || data.errors.length === 0) {
			responesMsg = message;
		}
		state.infoText = data.errors;

		return {
			globalSuccess: responesMsg,
		};
	}
};

const onBulkAssignPartnersImport = async (state: PropertyType, payload: PropertyType) => {
	const { token, bulkAssignPartnersForm } = payload;
	const formErrors = validateBulkAssignPartnersForm(bulkAssignPartnersForm);
	let responesMsg = '';
	if (formErrors) {
		return { formErrors };
	} else {
		const { message, data } = await bulkAssignPartners({
			token,
			bulkAssignPartnersForm,
		});

		if (!data.errors || data.errors.length === 0) {
			responesMsg = message;
		}
		state.infoText = data.errors;

		return {
			globalSuccess: responesMsg,
		};
	}
};

const onBulkEnableGamesImport = async (state: PropertyType, payload: PropertyType) => {
	const { token, bulkEnableGamesForm } = payload;
	const formErrors = validateEnableGamesForm(bulkEnableGamesForm);
	let responesMsg = '';
	if (formErrors) {
		return { formErrors };
	} else {
		const { message, data } = await bulkEnableGames({
			token,
			bulkEnableGamesForm,
		});

		if (!data.errors || data.errors.length === 0) {
			responesMsg = message;
		}
		state.infoText = data.errors;

		return {
			globalSuccess: responesMsg,
		};
	}
};

const onUpdateForm = (state: PropertyType, payload: PropertyType) => {
	const { field, value } = payload;
	if (field.includes('custom_fields')) {
		const fieldOptions = field.split('|');
		const id = fieldOptions[1];
		const option = fieldOptions[2];
		const currentCustomFields = [...state.gameForm.custom_fields];
		const updateFieldIndex = findIndex(state.gameForm.custom_fields, ['id', id]);
		currentCustomFields[updateFieldIndex][option] = value;
		const newForm = { ...state.gameForm, custom_fields: currentCustomFields };
		const errors = validateForm(newForm);
		const fieldError = errors ? errors[id as GameFormKey] : null;
		return { gameForm: newForm, formErrors: { ...state.formErrors, [id]: fieldError } };
	}
	const newForm = { ...state.gameForm, [field]: value };
	const errors = validateForm(newForm);
	const fieldError = errors ? errors[field as GameFormKey] : null;

	return {
		gameForm: newForm,
		formErrors: { ...state.formErrors, [field]: fieldError },
	};
};

const onGameTCategory = async (payload: PropertyType) => {
	const { token, filters } = payload;
	const resp = await getFilterOptions({ token, filters });

	return { ...resp.data };
};

const onGameSelect = async (payload: PropertyType) => {
	const { game } = payload;

	return {
		selectedGame: game,
		gameData: {},
		gameForm: {
			game_name: '',
			custom_game_id: '',
			notes: '',
			game_provider_id: [],
			provider_game_id_desktop: '',
			provider_game_id_mobile: '',
			aams_type_id: [],
			rtp: '',
			volatility: '',
			wagering_weight: '',
			is_slot: false,
			active: false,
			custom_fields: [],
		},
	};
};

const onAddGameCategory = async (payload: PropertyType) => {
	const { token, game_id, form } = payload;
	const resp = await addGameCategory({ token, game_id, form });

	return { ...resp.data };
};

const onAssignGamePartners = async (payload: PropertyType) => {
	const { token, game_id, form } = payload;
	try {
		const resp = await assignGamePartners({ token, game_id, form });
		return { ...resp.data, globalSuccess: ['Partners assigned successfully'] };
	} catch (error: any) {
		const errorMessage = error.message || '';
		return {
			formErrors: {
				partners: errorMessage,
			},
			globalErrors: [errorMessage],
		};
	}
};

const onAddNewField = (state: PropertyType) => {
	const currentFields = state.gameForm.custom_fields;
	return { gameForm: { ...state.gameForm, custom_fields: [...currentFields, { name: '', value: '', id: uuid() }] } };
};

const onRemoveField = (state: PropertyType, payload: PropertyType) => {
	const { field } = payload;
	const currentFields = state.gameForm.custom_fields.filter((f: PropertyType) => f.id !== field);
	return { gameForm: { ...state.gameForm, custom_fields: currentFields } };
};

const getAllPartners = async (payload: PropertyType) => {
	const { token, page } = payload;
	const perPage = 1000;
	const resp = await getCasinoGamePartners({ token, page, query: '', perPage });
	return { allSkins: resp };
};

const onGamesActivate = async (state: PropertyType, token: string) => {
	const success = await activateGames({
		token,
		gameIds: state.bulkRows,
	});

	return { globalErrors: [], globalSuccess: success };
};

const onGamesDeactivate = async (state: PropertyType, token: string) => {
	const success = await deactivateGames({
		token,
		gameIds: state.bulkRows,
	});

	return { globalErrors: [], globalSuccess: success };
};

const onRemovePartner = async (payload: PropertyType) => {
	const { token, gameId, partnerId } = payload;

	try {
		const resp = await unassignGamePartners({
			token,
			game_id: gameId,
			partner_id: partnerId,
		});

		return {
			globalSuccess: ['Partner removed successfully'],
			resp,
		};
	} catch (error: any) {
		const errorMessage = error.message || 'Failed to remove partner';
		return {
			globalErrors: [errorMessage],
		};
	}
};

export const actions: ActionsType = {
	LOADING: (state, payload) => ({ gamesLoading: payload }),
	ADD_FIELD: (state) => onAddNewField(state),
	REMOVE_FIELD: (state, payload) => onRemoveField(state, payload),
	GAMES_INIT: (state, payload) => onRequest(() => gamesInit(payload), true),
	PAGE_UPDATE: (state, payload) => onRequest(() => onPageUpdate(state, payload), true),
	PER_PAGE: (state, payload) => ({ perPage: payload }),
	UPDATE_COLUMNS: (state, payload) => ({ columns: payload }),
	PARTNERS_INIT: (state, payload) => partnersInit(payload),
	SELECT_GAME: (state, payload) => onGameSelect(payload),
	FILTERS_INIT: (state, payload) => onRequest(() => filtersInit(payload)),
	UPDATE_ROWS: (state, payload) => ({ rows: payload }),
	BULK_CHANGE: (state, payload) => onBulkChange(state, payload),
	BULK_ALL_CHANGE: (state) => onBulkAllChange(state),
	GAMES_ENABLE: (state, payload) => onGameToggle(payload),
	GAME_INIT: (state, payload) => gameInit(state, payload),
	GAMES_ADD: (state, payload) => onGameAdd(state, payload),
	GAMES_IMPORT: (state, payload) => onGamesImport(state, payload),
	GAMES_CATEGORIES: (state, payload) => onGameTCategory(payload),
	GAMES_CATEGORIES_ADD: (state, payload) => onAddGameCategory(payload),
	FILTER: (state, payload) => onFilter(state, payload),
	FORM: (state, payload) => onUpdateForm(state, payload),
	FORM_CATEGORY: (state, payload) => onUpdateCategoryForm(state, payload),
	FORM_RESET: () => formReset(),
	FORM_CATEGORY_RESET: () => ({ categoryForm: { categories: [] } }),
	FORM_PARTNERS_RESET: () => ({ activePartners: [], gameData: {}, gameForm: {} }),
	FORM_IMPORT_GAME_RESET: () => importFormReset(),
	FORM_BULK_ASSIGN_PARTNERS_RESET: () => bulkAssignPartnersFormReset(),
	FORM_BULK_ENABLE_GAMES_RESET: () => bulkEnableGamesFormReset(),
	UPDATE_FILTERS: (state, payload) => ({ filters: payload }),
	SAVING: (state, payload) => ({ savingData: payload }),
	GLOBAL_ERROR: (state, payload) => ({ globalErrors: payload }),
	GLOBAL_SUCCESS: (state, payload) => ({ globalSuccess: payload }),
	MESSAGE_INVALIDATE: () => ({
		globalErrors: [],
		globalSuccess: '',
		formErrors: '',
		usersLoading: false,
	}),
	INFO_TEXT: () => ({ infoText: '', formErrors: '' }),
	FILE_UPDATE: (state, payload) => ({ file: payload }),
	UPDATE_STATE: (state, payload) => {
		return { ...state, ...payload };
	},
	FORM_GAME_PARTNERS: (state, payload) => onUpdateGamePartnersForm(state, payload),
	PARTNERS_UPDATE: (state, payload) => ({ activePartners: payload }),
	GAME_PARTNERS_ASSIGN: (state, payload) => onAssignGamePartners(payload),
	GET_PARTNERS: (state, payload) => onRequest(() => getAllPartners(payload), true),
	ACTIVATE_GAMES: (state, payload) => onRequest(() => onGamesActivate(state, payload), true),
	DEACTIVATE_GAMES: (state, payload) => onRequest(() => onGamesDeactivate(state, payload), true),
	ASSIGN_PARTNERS_IMPORT: (state, payload) => onBulkAssignPartnersImport(state, payload),
	ENABLE_GAMES_IMPORT: (state, payload) => onBulkEnableGamesImport(state, payload),
	REMOVE_PARTNER: (state, payload) => onRemovePartner(payload),
};
